// src/components/BottomNav.tsx
//
// App-parity bottom navigation — mirrors the Flutter main_wrapper.dart nav:
// a dark curved bar with a raised center "Ask Aura" FAB (CosmicIcon), four
// side tabs (Home · Call | Reports · Remedies), pink active tint. Mobile-only
// (the desktop top Navbar stays), shown only for signed-in users, and HIDDEN
// on /chat exactly like the app (so it never covers the chat composer).

import { useLocation, useNavigate } from "react-router-dom";
import {
  IconHome2,
  IconPhone,
  IconFileText,
  IconShoppingBag,
} from "@tabler/icons-react";
import { useAuth } from "../context/AuthContext";
import CosmicIcon from "./CosmicIcon";
import styles from "./BottomNav.module.css";
import { SHOW_STORE } from "../utils/featureFlags";

type Tab = {
  key: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  match: (path: string, search: string) => boolean;
};

const LEFT: Tab[] = [
  {
    key: "home",
    label: "Home",
    icon: <IconHome2 size={24} />,
    to: "/",
    match: (p) => p === "/",
  },
  {
    key: "call",
    label: "Call",
    icon: <IconPhone size={24} />,
    to: "/chat?startCall=1",
    match: (p, s) => p === "/chat" && s.includes("startCall"),
  },
];

const RIGHT: Tab[] = [
  {
    key: "reports",
    label: "Reports",
    // Land on the Reports (AI) tab, not the default Charts tab.
    to: "/reports#ai",
    icon: <IconFileText size={24} />,
    match: (p) => p.startsWith("/reports"),
  },
  {
    key: "remedies",
    label: "Remedies",
    icon: <IconShoppingBag size={24} />,
    to: "/store",
    match: (p) => p.startsWith("/store"),
  },
];

export default function BottomNav() {
  const { user, currency, userCountry } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Remedies store is India-only — require BOTH INR pricing AND a India geo
  // signal so a non-Indian with a stale INR currency_code never sees it.
  const isIndia =
    currency === "INR" && (userCountry || "IN").toUpperCase() === "IN";

  // App parity: nav is hidden on the chat screen (so the composer is clear)
  // and on the wallet screen.
  const path = location.pathname;
  const search = location.search;
  if (path === "/chat" || path === "/wallet") return null;

  // Keep in sync with FOCUSED_LANDING_ROUTES in App.tsx. These are bought
  // traffic with a single CTA, and this nav sits on top of it.
  const FOCUSED_LANDINGS = [
    "/free-kundli-matching",
    "/career",
    "/love-astrology",
    "/marriage",
    "/foreign",
    "/bharat",
  ];
  if (FOCUSED_LANDINGS.includes(path.replace(/\/+$/, ""))) return null;

  // Only signed-in users see the app-style nav; logged-out marketing visitors
  // keep the top Navbar + Footer only. (CSS also hides it on desktop widths.)
  if (!user) return null;

  const renderTab = (t: Tab) => {
    const active = t.match(path, search);
    return (
      <button
        key={t.key}
        className={`${styles.item} ${active ? styles.itemActive : ""}`}
        onClick={() => navigate(t.to)}
        aria-label={t.label}
      >
        <span className={styles.itemIcon}>{t.icon}</span>
        <span className={styles.itemLabel}>{t.label}</span>
      </button>
    );
  };

  return (
    <nav className={styles.bar} aria-label="Primary">
      <div className={styles.side}>{LEFT.map(renderTab)}</div>

      {/* Center raised "Ask Aura" FAB → /chat. Animated CosmicIcon (orbiting
          electrons), white glow, no solid bg — exactly like the app. */}
      <button
        className={styles.fab}
        onClick={() => navigate("/chat")}
        aria-label="Ask Aura"
      >
        <span className={styles.fabCircle}>
          <CosmicIcon size={50} className={styles.cosmic} />
        </span>
        <span className={styles.fabLabel}>Ask Aura</span>
      </button>

      <div className={styles.side}>
        {RIGHT.filter(
          (t) => t.key !== "remedies" || (isIndia && SHOW_STORE)
        ).map(renderTab)}
      </div>
    </nav>
  );
}
